import { useQuery } from '@tanstack/react-query'

import { useApi } from '@fortem-monorepo/react-spa'
import type { AccruedInterests } from '@fortem-monorepo/web-sdk'

import { isFutureBalanceDate } from '@/lib/accrued-interest-dates'

export const accruedInterestKeys = {
  all: ['operations', 'accrued-interests'] as const,
  daily: (params: AccruedInterests.Params) => ['operations', 'accrued-interests', 'daily', params] as const,
  balances: (params: AccruedInterests.Params) => ['operations', 'accrued-interests', 'balances', params] as const,
  chartInstruments: (params: AccruedInterests.ChartFundParams) =>
    ['operations', 'accrued-interests', 'chart-instruments', params] as const,
  chart: (params: AccruedInterests.ChartParams) => ['operations', 'accrued-interests', 'chart', params] as const,
}

export const useAccruedChartInstruments = (params: AccruedInterests.ChartFundParams) => {
  const api = useApi()
  return useQuery({
    queryKey: accruedInterestKeys.chartInstruments(params),
    queryFn: () => api.accruedInterests.getChartInstruments(params),
    enabled: !!params.fund_name,
    refetchOnMount: true,
  })
}

export const useAccruedBalanceChart = (params: AccruedInterests.ChartParams) => {
  const api = useApi()
  return useQuery({
    queryKey: accruedInterestKeys.chart(params),
    queryFn: () => api.accruedInterests.getBalanceChart(params),
    enabled: !!params.fund_name && params.instrument_id > 0,
    refetchOnMount: true,
  })
}

export const useDailyAccruedInterests = (params: AccruedInterests.Params) => {
  const api = useApi()
  return useQuery({
    queryKey: accruedInterestKeys.daily(params),
    queryFn: () => api.accruedInterests.getDaily(params),
    enabled: !!params.fund_name && !!params.value_date,
  })
}

export const useAccruedInterestBalances = (params: AccruedInterests.Params) => {
  const api = useApi()
  return useQuery({
    queryKey: accruedInterestKeys.balances(params),
    queryFn: () => {
      if (isFutureBalanceDate(params.value_date)) {
        throw new Error('Balances cannot be computed for future dates (London time).')
      }

      return api.accruedInterests.getBalances(params)
    },
    enabled: !!params.fund_name && !!params.value_date && !isFutureBalanceDate(params.value_date),
    // Tabs unmount their content; refresh invalidated previews when returning.
    refetchOnMount: true,
  })
}
